"use client";
import { useAuth } from "@/contexts/AuthContext";
import useRecaptcha from "@/hooks/useRecaptcha";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link } from "@/i18n/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import SocialLogins from "./SocialLogins";
import { Button } from "./ui/Button";
import { Checkbox, Input } from "./ui/input";

export default function Register({
  onBackToLogin,
  hideLogin = false,
  invitationCode = null,
}) {
  const t = useTranslationDetector();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const { getCaptchaToken } = useRecaptcha();
  const { session, register: registerAuth } = useAuth();
  // Zod schema with dynamic messages from next-intl
  const schema = z
    .object({
      firstName: z
        .string()
        .min(2, t("validations.firstNameMin"))
        .max(50, t("validations.firstNameMax")),
      lastName: z
        .string()
        .min(2, t("validations.lastNameMin"))
        .max(50, t("validations.lastNameMax")),
      email: z
        .string()
        .email(t("validations.email"))
        .min(1, t("validations.emailmin"))
        .max(125, t("validations.emailmax")),
      password: z
        .string()
        .min(6, t("validations.passwordmin"))
        .max(50, t("validations.passwordmax")),
      confirmPassword: z.string().min(6, t("validations.confirmPasswordMin")),
      terms: z.boolean().refine((val) => val === true, {
        message: t("validations.termsRequired"),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("validations.passwordsNotMatch"),
      path: ["confirmPassword"],
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      terms: false,
    },
  });

  const singUp = async (data) => {
    setApiError(null);
    setLoading(true);
    try {
      const RecaptchaKey = await getCaptchaToken("signup");
      const requestParams = { ...data, RecaptchaKey };
      if (invitationCode) {
        requestParams.InvitationCode = invitationCode;
      }
      const response = await registerAuth(requestParams);

      if (!response.success) {
        setApiError(response.error);
      }
    } catch (error) {
      setApiError(t(`${error.response.data.Type}.${error.response.data.Name}`));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    singUp({
      ...data,
      Gender: "R",
      Password: data.password,
      PasswordConfirm: data.confirmPassword,
      AgreementAgreed: data.terms,
      LanguageCode: session.LanguageCode,
      CountryCode: session.CountryCode,
      CurrencyCode: session.CurrencyCode,
      AuthenticationType: 0,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={t("registermodal.firstName")}
          id="firstName"
          type="text"
          {...register("firstName")}
          error={errors.firstName?.message}
        />

        <Input
          label={t("registermodal.lastName")}
          id="lastName"
          type="text"
          {...register("lastName")}
          error={errors.lastName?.message}
        />
      </div>

      <Input
        label={t("registermodal.email")}
        id="email"
        type="email"
        {...register("email")}
        error={errors.email?.message}
      />

      <div className="flex flex-row gap-4">
        <Input
          label={t("registermodal.password")}
          id="password"
          type="password"
          {...register("password")}
          error={errors.password?.message}
        />

        <Input
          label={t("registermodal.confirmPassword")}
          id="confirmPassword"
          type="password"
          {...register("confirmPassword")}
          error={errors.confirmPassword?.message}
        />
      </div>

      <div>
        <Checkbox size="md" {...register("terms")}>
          <span>
            {t.rich("registermodal.termsAccept", {
              privacy: (chunks) => (
                <Link
                  href="/protection-of-personal-data"
                  className="text-primary"
                  target="_blank"
                >
                  {chunks}
                </Link>
              ),
              terms: (chunks) => (
                <Link
                  href="/terms-of-service"
                  className="text-primary"
                  target="_blank"
                >
                  {chunks}
                </Link>
              ),
            })}
          </span>
        </Checkbox>
        {errors.terms && (
          <p className="mt-2 text-sm text-red-600">{errors.terms.message}</p>
        )}
      </div>

      {apiError && <p className="text-sm text-red-600">{apiError}</p>}

      <Button type="submit" loading={loading} className="w-full" size="lg">
        {t("registermodal.register")}
      </Button>

      <div className="flex items-center justify-between gap-2">
        <span className="border-primary-weak w-full border-b"></span>
        <span className="px-2">{t("common.or")}</span>
        <span className="border-primary-weak w-full border-b"></span>
      </div>

      <SocialLogins invitationCode={invitationCode} />

      {!hideLogin && (
        <div className="flex items-center justify-center gap-2">
          <span>{t("registermodal.alreadyHaveAccount")}</span>
          <Button variant="link" color="primary" onClick={onBackToLogin}>
            {t("registermodal.login")}
          </Button>
        </div>
      )}
    </form>
  );
}
